import React from "react";
import { View, StyleSheet, Text, TouchableOpacity } from "react-native";
import { theme } from "../theme/theme";
import Icon from "./Icon";

/**
 * Offered when `useAppUpdate` spots a newer build on the server.
 *
 * Sits at the bottom because the orange AppHeader owns the top of every screen
 * and this must never cover the navigation icons in it. Position is fixed, not
 * absolute, so it stays put while a long listing scrolls underneath.
 *
 * Dismissable on purpose. A member reading a message does not want a bar they
 * cannot get rid of, and the next relaunch picks the build up anyway.
 */
const UpdateBanner = ({ visible, onUpdate, onDismiss }) => {
  if (!visible) return null;

  return (
    <View style={styles.wrap} pointerEvents="box-none">
      <View style={styles.bar}>
        <Icon name="refresh" size={20} color={theme.colors.onPrimary} />
        <Text style={styles.text}>A newer version is ready</Text>

        <TouchableOpacity
          onPress={onUpdate}
          style={styles.action}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel="Update now"
          accessibilityHint="Reloads the app to the newest version"
        >
          <Text style={styles.actionText}>Update</Text>
        </TouchableOpacity>

        <TouchableOpacity
          onPress={onDismiss}
          style={styles.close}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel="Dismiss"
          accessibilityHint="Hides this until next time you open the app"
        >
          <Icon name="close" size={18} color={theme.colors.onPrimary} />
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: "center",
    paddingHorizontal: theme.spacing.md,
    // Clears the home-bar area on an iPhone in standalone mode, where
    // viewport-fit=cover means the page runs right to the bottom edge.
    paddingBottom: "max(16px, env(safe-area-inset-bottom))",
    zIndex: 900,
  },
  bar: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.eucalyptus,
    borderRadius: 28,
    paddingVertical: theme.spacing.sm,
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.sm,
    elevation: 6,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 6,
  },
  text: {
    fontSize: 14,
    fontFamily: "Poppins",
    color: theme.colors.onPrimary,
  },
  action: {
    backgroundColor: "rgba(255,255,255,0.22)",
    borderRadius: 18,
    paddingVertical: 6,
    paddingHorizontal: theme.spacing.md,
  },
  actionText: {
    fontSize: 14,
    fontWeight: "600",
    fontFamily: "Poppins",
    color: theme.colors.onPrimary,
  },
  close: {
    width: 32,
    height: 32,
    alignItems: "center",
    justifyContent: "center",
  },
});

export default UpdateBanner;
